
import { smartInsightsLabelMap, showHideLabels  } from "./constants";

import { getShortLocationLabel } from "../globalutils";
import { ALL_NATIONS_OPTIONS, channelWiseNationsMap } from "./constants";
import { hasValidFilterAdded } from "../validReqParam/validReqParam";
import { featureFlags } from "../constants";
import { generateNationalityCluster } from "../nationality/nationality";
import { getClusteredIndustries } from "../industry";
import { getClusteredLocations } from "../location";
import { getClusteredDegrees, getClusteredInstitutes } from "../education";
import { NationalityConstants } from "../nationality/constants";
import { MASTER_DATA } from "./mockMasterData";
  
  function concatenateAllValues(inputObject: any): string {
      let result = '';
    
      function formatKey(key: string): string {
        return key
          .replace(/_/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .toLowerCase();
      }
    
      function traverse(value: any, key?: string): void {
        if (value == null) return;
    
        // Skip the 'type' key entirely
        if (key === 'type') {
          return;
        }
    
        if (typeof value === 'object') {
          if (Array.isArray(value)) {
            value.forEach(item => traverse(item));
          } else {
            Object.entries(value).forEach(([nestedKey, nestedValue]) =>
              traverse(nestedValue, nestedKey)
            );
          }
        } else if (typeof value === 'boolean' || value === 'yes' || value === 'no') {
          if (key) {
            const formattedKey = formatKey(key);
            const formattedValue = typeof value === 'boolean' ? value.toString() : value;
            const newEntry = `${formattedKey}: ${formattedValue}`;
            result = result ? `${result}, ${newEntry}` : newEntry;
          }
        } else {
          result = result ? `${result}, ${value}` : value;
        }
      }
    
      traverse(inputObject);
      return result.trim() || "Blank Search";
  }
  
  type FilterValue = string | number | boolean | object | any[];
  
  interface TransformedSearchData {
    label: string;
    title: string;
    requestId: string;
    recentSearchId?: number;
    filter: Record<string, FilterValue>;
    reqParam: Record<string, any>;
  }
  
  // Helper Types
  type KeywordType = 'all' | 'any' | 'exclude' | 'boolean';
  type Filters = Record<string, any>;
  type Queries = Record<string, any>;
  
  // Helper functions for data extraction
  const extractKeywords = (queries: Queries, refine_search: any): FilterValue[] => {
      let keywords: FilterValue[] = [];
  
      const keywordTypes: KeywordType[] = ['all', 'any', 'exclude', "boolean"];
      keywords = keywordTypes.flatMap((type) => {
          const val = queries?.[type];
          if (!val) return [];
          return typeof val === 'string'
              ? val.split(',')
                  .map((key: string) => key.trim())
                  .filter(Boolean)
                  .map((key: string) => ({ name: key, type }))
              : [];
      });
  
      // Process keywords from refine_search
      if (refine_search?.keywords && Array.isArray(refine_search.keywords)) {
          const refineSearchKeywords = refine_search.keywords.flatMap((keywordObj: any) => {
              if (!keywordObj?.keywords || !keywordObj?.condition) return [];
              
              return keywordObj.keywords.split(',')
                  .map((key: string) => key.trim())
                  .filter(Boolean)
                  .map((key: string) => ({ 
                      name: key, 
                      type: keywordObj.condition as KeywordType 
                  }));
          });
  
          keywords = [...keywords, ...refineSearchKeywords];
      }
      return keywords;
  };
  
  const extractCompanyFilters = (filters: Filters): FilterValue[] => {
      const keywordTypes: KeywordType[] = ['all', 'any', 'exclude'];
      return keywordTypes.flatMap((type) => {
          const companies = filters.company?.[type];
          if (!Array.isArray(companies)) return [];
          return companies
              .filter(Boolean)
              .map((name: string) => ({ name: name.trim(), type }))
              .filter(item => item.name.length > 0);
      });
  };
  
  export const getYearMonths = (exp: any) =>{
      const [years, months] = exp?.split(".");
      return {years, months: (months === "1" || months === "10") ? "10" : months ? String(Number(months)) : ""};
  }
  
  export const formatExperience = (exp: any): string | null => {
      const experience = {
          min_exp: exp?.min_exp === "null" ? null : exp?.min_exp,
          max_exp: exp?.max_exp === "null" ? null : exp?.max_exp,
      };
  
       if (typeof experience.min_exp == "number") {
          if(experience.min_exp === 0) return "Fresher";
          
          //  const years = Math.floor(experience.min_exp);
          //  const months = Math.round((experience.min_exp - years) * 12);
          //  const yearStr = years === 1 ? "1 yr" : years > 1 ? `${years} yrs` : "";
          //  const monthStr = months === 1 ? "1 mo" : months > 1 ? `${months} mos` : "";
          //  return [yearStr, monthStr].filter(Boolean).join(" ");
       }
  
      const { min_exp, max_exp } = experience ?? {};
  
       const { years: fromYears, months: fromMonth } = getYearMonths(`${min_exp}`);
       const { years: toYears, months: toMonth } = getYearMonths(`${max_exp}`);
  
       // When either side has months (format: "2y 3m – 4y 10m")
       if (min_exp && max_exp && (fromMonth || toMonth) && (fromMonth !== "0" || toMonth !== "0")) {
          
         const fromLabel = `${fromYears}y${fromMonth ? ` ${fromMonth}m` : ""}`;
         const toLabel = `${toYears}y${toMonth ? ` ${toMonth}m` : ""}`;
         return `${fromLabel} to ${toLabel}`;
       }
  
       if (min_exp && !max_exp && fromMonth && fromMonth !== "0") {
         const fromLabel = `${fromYears}y${fromMonth ? ` ${fromMonth}m` : ""}`;
         return `${fromLabel} +`;
       }
  
       if (!min_exp && max_exp && toMonth && toMonth !== "0") {
         const toLabel = `${toYears}y${toMonth ? ` ${toMonth}m` : ""}`;
         return `Up to ${toLabel}`;
       }
  
      // Handle case where min_exp is missing but max_exp exists
      if (!min_exp && max_exp) {
          return `Less than ${max_exp} years`;
      }
  
      // Handle case where max_exp is missing but min_exp exists
      if (min_exp && !max_exp) {
          return `More than ${min_exp} years`;
      }
  
      // Handle case where both exist
      if (min_exp && max_exp) {
          return `${min_exp} to ${max_exp} years`;
      }
  
      // Handle case if both are 0
      if(Number(min_exp) === 0 && Number(max_exp) === 0){
          return `0 to 0 years`
      }
      
      return null;
  };
  
  const formatGender = (additional: any): string | null => {
      const gender = additional?.gender;
      if (typeof gender === 'string' && gender.trim()) {
          if (gender.trim().toLowerCase() === "any") {
              return "Male/Female"
          }
          return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
      }
      return null;
  };
  
  const formatSalary = (company: any): string | null => {
      const { min_ctc, max_ctc, currency } = company ?? {};
      const formatValue = (value: number) => {
          if (!isFinite(value)) return '0';
          if (value >= 100000) {
              return `${(value / 100000).toFixed(2)}L`;
          } else if (value >= 1000) {
              return `${(value / 1000).toFixed(0)}K`;
          }
          return value.toString();
      };
  
      // Handle case where min_ctc is missing but max_ctc exists
      if (!min_ctc && typeof max_ctc === 'number' && !isNaN(max_ctc) && max_ctc !== 0) {
          return `${currency || 'INR'} Less than ${formatValue(max_ctc)}`;
      }
  
      // Handle case where max_ctc is missing but min_ctc exists
      if (typeof min_ctc === 'number' && !isNaN(min_ctc) && min_ctc !== 0 && !max_ctc) {
          return `${currency || 'INR'} More than ${formatValue(min_ctc)}`;
      }
  
      // Handle case where both exist
      if (
          typeof min_ctc === 'number' && 
          typeof max_ctc === 'number' && 
          !isNaN(min_ctc) && 
          !isNaN(max_ctc) && 
          min_ctc !== 0 && 
          max_ctc !== 0
      ) {
          return `${currency || 'INR'} ${formatValue(min_ctc)} to ${formatValue(max_ctc)}`;
      }
  
      return null;
  };
  
  const extractLanguages = (nationality: any): { name: string; type: KeywordType }[] => {
      // First try to get languages from langs_known_1 and langs_known_2
      const langsKnown1 = nationality?.langs_known_1 ?? [];
      const langsKnown2 = nationality?.langs_known_2 ?? [];
      
      if (langsKnown1.length > 0 || langsKnown2.length > 0) {
          // Combine and deduplicate languages from both arrays
          return [...new Set([
              ...langsKnown1,
              ...langsKnown2
          ])]
          .filter((lang: any) => Boolean(typeof lang === 'string' && lang.trim()))
          .map((lang: string) => ({ name: lang.trim(), type: 'any' as KeywordType }));  // Default type as 'any' for old format
      }
  
      // If old format is not available, try the new languages format
      if (!nationality?.languages) return [];
      
      const keywordTypes: KeywordType[] = ['all', 'any', 'exclude'];
      return keywordTypes.flatMap(type => {
          const languages = nationality.languages[type] ?? [];
          return languages
              .filter((lang: any) => Boolean(typeof lang === 'string' && lang.trim()))
              .map((lang: string) => ({ name: lang.trim(), type }));
      });
  };
  
  const extractIndustries = (industry: any, masterData: any): string[] => {
      const industries = industry?.industries ?? [];
      if (Array.isArray(industries) && industries.length) {
          const industriesList = industries
              .filter(industry => typeof industry === 'string' && industry.trim())
              .map(industry => industry.trim());
          const industryCluster = getClusteredIndustries({ industries: industriesList }, masterData?.topIndustryClusters)
          return industryCluster?.industries;
      }
      return [];
  };
  
  const extractDesignations = (company: any, masterData: any): Record<string, string[]> | null => {
      let hasValues = false;
      const result: Record<string, string[]> = {};
      
      // Handle designations
      const designations = company?.designations ?? [];
      if (designations.length > 0) {
          const cleanDesignations = designations
              .filter((designation: any): boolean => 
                  typeof designation === 'string' && Boolean(designation.trim()))
              .map((designation: string) => designation.trim());
             
        if (cleanDesignations.length > 0) {
            result.designations = cleanDesignations;
            hasValues = true;
        }
      }
      
      // Handle designation categories
      const designationCategories = company?.designation_categories ?? [];
      if (designationCategories.length > 0) {
          const cleanCategories = designationCategories
              .filter((category: any): boolean => 
                  typeof category === 'string' && Boolean(category.trim()))
              .map((category: string) => category.trim());
              
          if (cleanCategories.length > 0) {
              result.designation_categories = cleanCategories;
              hasValues = true;
          }
      }
      return hasValues ? result : null;
  };
  
  const formatNoticePeriod = (company: any): string | null => {
      const days = company?.notice_period;
      const isImmediateJoiner = company?.immediate_joiner === 1 || company?.immediate_joiner === true;
      
      if (isImmediateJoiner) {
          return 'Immediate Joiner';
      }
  
      if (typeof days === "number" && !isNaN(days)) {
          if (days <= 30) {
              return 'Upto 30 days';
          }else if (days <= 45) {
              return 'Upto 45 days';
          }else if (days <= 60) {
              return 'Upto 60 days';
          }else if (days <= 90) {
              return 'Upto 90 days';
          }else {
              return 'Any';
          }
      }
      
      return null;
  };
  
  const extractLocations = (location: any, masterData: any): Record<string, string[]> => {
      const result: Record<string, string[]> = {};
      const current = getClusteredLocations({current_locations: location?.current_locations ?? []}, masterData, "current")?.current_locations ?? []
      const preferred = getClusteredLocations({preferred_locations: location?.preferred_location ?? []}, masterData, "preferred")?.preferred_locations ?? []
      // Handle current locations
      if (Array.isArray(current) && current.length) {
         result.location = current.map((loc) => getShortLocationLabel(loc.trim()));
      }
  
      // Handle preferred locations    
      if (Array.isArray(preferred) && preferred.length && location?.preferred_locations_type !== "same") {       
         result['Preferred Location'] = preferred.map((loc) => getShortLocationLabel(loc.trim()));
      }
      return result;
  };
  
  const convertToBoolean = (value: any): boolean => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value === 1;
      return false;
  };
  
  const extractNationalityFields = (nationality: any, cn: number): Record<string, any> => {
      const result: Record<string, any> = {};
      if (!nationality) return result;
  
      // Handle nationalities
      if (Array.isArray(nationality.nationalities) && nationality.nationalities.length > 0) {
          const nationalities = generateNationalityCluster(nationality.nationalities, {
              allNationsOptions: ALL_NATIONS_OPTIONS,
              allNationsMaps: channelWiseNationsMap(cn)
            });
          if (nationalities.length) {
              result.nationalities = 
              nationalities.map((nat: string) => NationalityConstants[nat.toUpperCase() as Uppercase<string>] || nat);
          }
          //   return generateNationalityCluster(nationalities, {
          //     allNationsOptions: ALL_NATIONS_OPTIONS,
          //     allNationsMaps: nationsMap
          //   });
          // let cleanNats = nationality.nationalities
          //     .filter((nat: any) => typeof nat === 'string' && nat.trim())
          //     .map((nat: string) => nat.trim());
              
          // if (cleanNats.length > 0) {
          //     cleanNats = generateNationalityCluster(cleanNats, masterData);
          //     result.nationalities = cleanNats.map((nat: string) => NationalityConstants[nat.toUpperCase() as Uppercase<string>] || nat);
          // }
      }
  
      // Handle work authorization
      if (Array.isArray(nationality.us_work_auth) && nationality.us_work_auth.length > 0) {
          result.visa_status = nationality.us_work_auth
              .filter((auth: any) => typeof auth === 'string' && auth.trim())
              .map((auth: string) => auth.trim());
      }
  
      // Handle visa status
      if (Array.isArray(nationality.visa_status) && nationality.visa_status.length > 0) {
          result.visa_status = nationality.visa_status
              .filter((visa: any) => typeof visa === 'string' && visa.trim())
              .map((visa: string) => visa.trim());
      }
  
      // Handle languages with priority order
      const languages = extractLanguages(nationality);
      if (languages.length > 0) {
          result.languages = languages;
      }
  
      return result;
  };
  
  const extractEducation = (education: any, masterData: any, channel_id: number): string[] => {
    if (!education) return [];

    /** Preserves insertion order; dedupes degrees/specs so the same label is not repeated unnecessarily. */
    const orderedLabels: string[] = [];
    const seenNonInstitution = new Set<string>();

    const addDeduped = (raw: string): void => {
        const s = raw.trim();
        if (!s || seenNonInstitution.has(s)) return;
        seenNonInstitution.add(s);
        orderedLabels.push(s);
    };

    /**
     * Institution cluster labels from specific_ug / specific_pg / specific_ppg are pushed every time
     * they appear, so the same cluster selected on multiple levels shows once per level in save/recent UI.
     * (A global Set would collapse them to a single entry.)
     */
    const addInstitutionLabel = (raw: string): void => {
        const s = raw.trim();
        if (!s) return;
        orderedLabels.push(s);
    };

    // Helper function to process degree and specialization combinations
    const processDegreeSpecs = (degrees: any[], type: string): void => {
        if (!Array.isArray(degrees)) return;
        const modifiedDegree = getClusteredDegrees(degrees, masterData, channel_id, type)
        modifiedDegree.forEach(degreeObj => {
            if (!degreeObj || typeof degreeObj !== 'object') return;

            // Add degree
            if (typeof degreeObj.degree === 'string' && degreeObj.degree.trim() && Array.isArray(degreeObj.specializations) && degreeObj.specializations?.length === 0) {
                addDeduped(degreeObj.degree);
            }

            // Add specializations
            if (Array.isArray(degreeObj.specializations) && degreeObj.specializations?.length > 0) {
                degreeObj.specializations.forEach((spec: any) => {
                    if (typeof spec === 'string' && spec.trim()) {
                        addDeduped(spec.trim());
                    }
                });
            }
        });
    };

    // Helper function to process institutions
    const processInstitutions = (institutions: any[],): void => {
        if (!Array.isArray(institutions)) return;
        const modifiedInstitution = getClusteredInstitutes(institutions, masterData)
        modifiedInstitution.forEach(inst => {
            if (typeof inst === 'string' && inst.trim()) {
                addInstitutionLabel(inst);
            }
        });
    };

    // Process UG education
    if (!education.no_ug) {
        if (education.specific_ug) {
            processDegreeSpecs(education.specific_ug.degrees, "ug");
            processInstitutions(education.specific_ug.institutions);
        }
    }

    // Process PG education
    if (!education.no_pg) {
        if (education.specific_pg) {
            processDegreeSpecs(education.specific_pg.degrees, "pg");
            processInstitutions(education.specific_pg.institutions);
        }
    }

    // Process PPG education
    if (!education.no_ppg) {
        if (education.specific_ppg) {
            processDegreeSpecs(education.specific_ppg.degrees, "ppg");
            processInstitutions(education.specific_ppg.institutions);
        }
    }

    // Process highest education if available (for backward compatibility)
    if (Array.isArray(education.highest)) {
        education.highest.forEach((item: any) => {
            if (item?.degree && typeof item.degree === 'string') {
                addDeduped(item.degree);
            }
            if (Array.isArray(item?.specializations)) {
                item.specializations.forEach((spec: any) => {
                    if (typeof spec === 'string' && spec.trim()) {
                        addDeduped(spec.trim());
                    }
                });
            }
            if (item?.institution && typeof item.institution === 'string') {
                addDeduped(item.institution.trim());
            }
        });
    }

    return orderedLabels;
   };
//   const extractEducation = (education: any, masterData: any, channel_id: number): string[] => {
//       if (!education) return [];
  
//       const educationInfo: Set<string> = new Set();
  
//       // Helper function to process degree and specialization combinations
//       const processDegreeSpecs = (degrees: any[], type: string): void => {
//           if (!Array.isArray(degrees)) return;
//           const modifiedDegree = getClusteredDegrees(degrees, masterData, channel_id, type)
//           modifiedDegree.forEach(degreeObj => {
//               if (!degreeObj || typeof degreeObj !== 'object') return;
  
//               // Add degree
//               if (typeof degreeObj.degree === 'string' && degreeObj.degree.trim() && Array.isArray(degreeObj.specializations) && degreeObj.specializations?.length === 0) {
//                   educationInfo.add(degreeObj.degree.trim());
//               }
  
//               // Add specializations
//               if (Array.isArray(degreeObj.specializations) && degreeObj.specializations?.length > 0) {
//                   degreeObj.specializations.forEach((spec: any) => {
//                       if (typeof spec === 'string' && spec.trim()) {
//                           educationInfo.add(spec.trim());
//                       }
//                   });
//               }
//           });
//       };
  
//       // Helper function to process institutions
//       const processInstitutions = (institutions: any[],): void => {
//           if (!Array.isArray(institutions)) return;
//           const modifiedInstitution = getClusteredInstitutes(institutions, masterData)
//           modifiedInstitution.forEach(inst => {
//               if (typeof inst === 'string' && inst.trim()) {
//                   educationInfo.add(inst.trim());
//               }
//           });
//       };
  
//       // Process UG education
//       if (!education.no_ug) {
//           if (education.specific_ug) {
//               processDegreeSpecs(education.specific_ug.degrees, "ug");
//               processInstitutions(education.specific_ug.institutions);
//           }
//       }
  
//       // Process PG education
//       if (!education.no_pg) {
//           if (education.specific_pg) {
//               processDegreeSpecs(education.specific_pg.degrees, "pg");
//               processInstitutions(education.specific_pg.institutions);
//           }
//       }
  
//       // Process PPG education
//       if (!education.no_ppg) {
//           if (education.specific_ppg) {
//               processDegreeSpecs(education.specific_ppg.degrees, "ppg");
//               processInstitutions(education.specific_ppg.institutions);
//           }
//       }
  
//       // Process highest education if available (for backward compatibility)
//       if (Array.isArray(education.highest)) {
//           education.highest.forEach((item: any) => {
//               if (item?.degree && typeof item.degree === 'string') {
//                   educationInfo.add(item.degree.trim());
//               }
//               if (Array.isArray(item?.specializations)) {
//                   item.specializations.forEach((spec: any) => {
//                       if (typeof spec === 'string' && spec.trim()) {
//                           educationInfo.add(spec.trim());
//                       }
//                   });
//               }
//               if (item?.institution && typeof item.institution === 'string') {
//                   educationInfo.add(item.institution.trim());
//               }
//           });
//       }
  
//       return Array.from(educationInfo);
//   };
  
  const processBooleanFields = (company: any): Record<string, FilterValue> => {
      const result: Record<string, FilterValue> = {};
      const booleanFields: string[] = [];
      
      booleanFields.forEach(key => {
          const val = company?.[key];
          if (val === 0 || val === 1) {
              result[key] = Boolean(val);
          } else if (typeof val === "boolean") {
              result[key] = val;
          }
      });
  
      return result;
  };
  
  const formatInsightKey = (key: string): string => {
      return key
          .replace(/^is_/i, '') // Remove 'is_' prefix
          .split('_')
          .map((word, index) => {
              // Special cases for numbers and abbreviations
              if (/^\d+$/.test(word)) return word; // Keep numbers as is
              if (/^[A-Z]+$/.test(word)) return word; // Keep abbreviations uppercase
              // Capitalize first word and words after numbers
              if (index === 0 || /^\d+$/.test(key.split('_')[index - 1])) {
                  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
              }
              return word.toLowerCase();
          })
          .join(' ');
  };
  
  const processInsightFields = (filters: Filters): Record<string, string[]> => {
      const result: Record<string, string[]> = {};
      const insightFields = [
          "work_experience_insights",
          "experience_insight", 
          "personal_insight",
          "education_insight"
      ];
      
      let smartInsights: string[] = [];    
      insightFields.forEach(key => {
          const insightObj = filters[key];
          if (insightObj && typeof insightObj === "object") {
              // Handle both direct boolean values and nested objects
              const trueKeys = Object.entries(insightObj)
                  .filter(([_, value]) => {
                      // Handle nested objects
                      if (typeof value === 'object' && value !== null) {
                          return Object.values(value).some(v => v === true);
                      }
                      // Handle direct boolean values
                      return value === true;
                  })
                  .map(([key]) => {
                      if (smartInsightsLabelMap[key as keyof typeof smartInsightsLabelMap]) {
                          return smartInsightsLabelMap[key as keyof typeof smartInsightsLabelMap];
                      }
                      return formatInsightKey(key);
                  })
                  .filter(Boolean); // Remove any empty strings
                  
              if (trueKeys.length) {
                  smartInsights = [...smartInsights, ...trueKeys];
              }
          }
      });
  
      if (smartInsights.length > 0) {
          result['smart_insights'] = smartInsights;
      }
  
      return result;
  };
  
  const createOrderedFilter = (filter: Record<string, FilterValue>): Record<string, FilterValue> => {
      const priorityKeys = [
          "keywords",
          "ai_prompt",
          "location",
          "preferred_location",
          "experience",
          "salary",
          "notice_period",
          "education",
          "industries",
          "companies",
          "designation",
          "gender",
          "disability_type",
          "company_funding_status",
          "show_only_candidates_with",
          "hide_only_candidates_with",
          "smart_insights"
      ];
  
      const orderedFilter: Record<string, FilterValue> = {};
      
      // First add priority keys in specific order
      priorityKeys.forEach(key => {
          if (key in filter) {
              orderedFilter[key] = filter[key];
          }
      });
  
      // Then add remaining keys
      Object.entries(filter).forEach(([key, value]) => {
          if (!priorityKeys.includes(key)) {
              orderedFilter[key] = value;
          }
      });
  
      return orderedFilter;
  };
  
  const formatRangeValue = (from: number | undefined, to: number | undefined, unit: string = '', fromKey : "employee_count" | "age" = "employee_count"): string | null => {
  
      const updatedTo = to && to > 60 ? 61 : to;
      const updatedFrom = from && from > 60 ? 60 : from;
      const updatedToLabel = updatedTo && updatedTo > 60 ? "60+" : updatedTo;
      // Handle case where from is missing but to exists
      if (!updatedFrom && typeof updatedTo === 'number' && !isNaN(updatedTo)) {
          if (fromKey === "age") {
              // return `Less than ${updatedToLabel} years`.trim();
              return `Up to ${updatedToLabel} years`.trim();
          }
          return `Less than ${updatedToLabel} ${unit}`.trim();
      }
  
      // Handle case where to is missing but from exists
      if (typeof updatedFrom === 'number' && !isNaN(updatedFrom) && !updatedTo) {
          if (fromKey === "age") {
              return `${updatedFrom}+ years`.trim();
          }
          return `More than ${updatedFrom} ${unit}`.trim();
      }
      // Handle case where both exist
      if (typeof updatedFrom === 'number' && typeof updatedTo === 'number' && !isNaN(updatedFrom) && !isNaN(updatedTo)) {
          if (updatedFrom == 60 && updatedTo > 60) {
              return `60+ ${unit}`.trim();
          }
          return `${updatedFrom} to ${updatedToLabel} ${unit}`.trim();
      }
  
      return null;
  };
  
  const extractAdditionalFields = (additional: any): Record<string, any> => {
      const result: Record<string, any> = {};
  
      if (!additional) return result;
      // Handle age range
      const ageRange = formatRangeValue(additional.age_from, additional.age_to, 'Years', "age");
      if (ageRange) {
          result.Age = ageRange;
      }
  
      // Handle employee count range
      const empCountRange = formatRangeValue(
          additional.company_emp_count_from,
          additional.company_emp_count_to,
          'Employees',
          "employee_count"
      );
      if (empCountRange) {
          result.employee_count = empCountRange;
      }
  
      // Handle job type with proper formatting
      if (additional.job_type) {
          const formatJobType = (type: string): string => {
              // Split by underscore and space
              const words = type.split(/[_\s]+/);
              // Capitalize each word and join
              return words
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ');
          };
          result.job_type = formatJobType(additional.job_type);
      }
  
      // Handle disability type
      if (additional.disability_type || additional.dAbled) {
          const disabilityTypes: string[] = [];
          
          // Handle disability_type if it's a string
          if (typeof additional.disability_type === 'string' && additional.disability_type.trim()) {
              disabilityTypes.push(
                  additional.disability_type
                      .split(/[_\s]+/)
                      .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ')
              );
          }
          
          // Handle dAbled object if present
          if (additional.dAbled && typeof additional.dAbled === 'object') {
              if (additional.dAbled.child && typeof additional.dAbled.child === 'string') {
                  disabilityTypes.push(
                      additional.dAbled.child
                          .split(/[_\s]+/)
                          .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(' ')
                  );
              }
          }
          
          if (disabilityTypes.length > 0) {
              result.disability_type = disabilityTypes;
          }
      }
  
  
      // Handle gender
      if (additional.gender && additional.gender !== 'any') {
          result.gender = additional.gender.charAt(0).toUpperCase() + additional.gender.slice(1).toLowerCase();
      }
  
      // Handle company funding status
      if (Array.isArray(additional.company_funding_status) && additional.company_funding_status.length > 0) {
          result.company_funding_status = additional.company_funding_status;
      }
  
      return result;
  };
  
  const extractShowHideConditions = (additional: any, refine_search: any): { show_only_candidates_with: string[], hide_only_candidates_with: string[] } => {
      const result = {
          show_only_candidates_with: [] as string[],
          hide_only_candidates_with: [] as string[]
      };
  
      // Handle conditions from additional
      if (additional) {
          // Show only conditions from additional
          const additionalShowOnlyConditions = {
              show_only_verified_email: (additional.show_only_verified_email),
              show_only_verified_mobile: convertToBoolean(additional.show_only_verified_mobile),
              show_resumes_not_viewed: convertToBoolean(additional.show_resumes_not_viewed)
          };
  
          // Hide only conditions from additional
          const additionalHideOnlyConditions = {
              exclude_confidential_resume: convertToBoolean(additional.exclude_confidential_resume)
          };
  
          // Add true conditions to show_only array
          Object.entries(additionalShowOnlyConditions)
              .filter(([_, value]) => value)
              .forEach(([key, _]) => {
                  result.show_only_candidates_with.push(showHideLabels[key as keyof typeof showHideLabels] ?? key);
              });
  
          // Add true conditions to hide_only array
          Object.entries(additionalHideOnlyConditions)
              .filter(([_, value]) => value)
              .forEach(([key, _]) => {
                  result.hide_only_candidates_with.push(showHideLabels[key as keyof typeof showHideLabels] ?? key);
              });
      }
  
      // Handle conditions from refine_search
      if (refine_search) {
          // Show only conditions from refine_search
          if (convertToBoolean(refine_search.only_confidential_users)) {
              result.show_only_candidates_with.push(showHideLabels[ 'only_confidential_users']);
          }
  
          // Hide only conditions from refine_search
          const refineSearchHideConditions = {
              exclude_sms_sent: convertToBoolean(refine_search.exclude_sms_sent),
              exclude_email_sent: convertToBoolean(refine_search.exclude_email_sent),
              exclude_downloaded: convertToBoolean(refine_search.exclude_downloaded)
          };
  
          // Add true conditions to hide_only array
          Object.entries(refineSearchHideConditions)
              .filter(([_, value]) => value)
              .forEach(([key, _]) => {
                  result.hide_only_candidates_with.push(showHideLabels[key as keyof typeof showHideLabels] ?? key);
              });
      }
  
      return result;
  };
  
  export const transformSearchData = (dataArray: any[], msData?: Record<string, unknown>): TransformedSearchData[] => {
      const filtereddataArray = dataArray.filter((listItem) => {
          return !featureFlags.blankSearchEnabled ? hasValidFilterAdded(listItem.reqParam) : true
      })

      return filtereddataArray.map((data) => {
          try {
              const masterData = msData || (MASTER_DATA as any)?.[data?.reqParam?.channel_id] || {};

              const label = data?.label ?? data?.parameters ?? "";
              const title = data?.searchName ?? "";
              const requestId = data?.requestId ?? data?.searchId ?? "";
              const recentSearchId = data?.recentSearchId ?? data?.recentSearchId ?? 0;
              const reqParam = data?.reqParam ?? {};
              const filters = reqParam.filters ?? {};
              const queries = reqParam.queries ?? {};
              const refine_search = reqParam.refine_search ?? {};
              const channel_id = reqParam.channel_id
              const aiSearchData = reqParam.aiSearchData ?? {}; 
              const filter: Record<string, FilterValue> = {};
  
              // Extract and process all filter data
              const keywords = extractKeywords(queries, refine_search);
              if (keywords.length) filter.keywords = keywords;
  
              const aiQuery = aiSearchData.query ?? "";
              if (aiQuery && typeof aiQuery === "string" && featureFlags.isAiSearchEnabled) {
                  filter.ai_prompt = aiQuery;
              }
              const experience = formatExperience(filters.experience);
              if ((filters?.experience?.min_exp || filters?.experience?.max_exp)&& experience) filter.experience = experience;
  
              const gender = formatGender(filters.additional);
              if (gender) filter.gender = gender;
  
              const salary = formatSalary(filters.company);
              if (salary) filter.salary = salary;
  
              const languages = extractLanguages(filters.nationality);
              if (languages.length) filter.languages = languages;
  
              const industries = extractIndustries(filters.industry, masterData);
              if (industries.length) filter.industry = industries;
  
              // Handle designations and designation categories
              const designationData = extractDesignations(filters.company, masterData);
              if (designationData) {
                  if (designationData.designations) {
                      filter.designations = designationData.designations;
                  }
              }
  
              const noticePeriod = formatNoticePeriod(filters.company);
              if (noticePeriod) filter.notice_period = noticePeriod;
  
              const locations = extractLocations(filters.location, masterData);
              if (locations.location) filter.location = locations.location;
              if (locations['Preferred Location']) filter['Preferred Location'] = locations['Preferred Location'];
  
              // Process additional data
              const nationalityFields = extractNationalityFields(filters.nationality, channel_id);
              if (Object.keys(nationalityFields).length > 0) {
                  Object.assign(filter, nationalityFields);
              }
              const companyFilters = extractCompanyFilters(filters);
              if (companyFilters.length) filter.company = companyFilters;
  
              const education = extractEducation(filters.education, masterData, channel_id);
              if (education.length) filter.education = education;
  
              Object.assign(filter, processBooleanFields(filters.company));
              Object.assign(filter, processInsightFields(filters));
  
              const additionalFields = extractAdditionalFields(filters.additional);
              if (Object.keys(additionalFields).length > 0) {
                  Object.assign(filter, additionalFields);
              }
  
              // Add show_only and hide_only conditions
              const showHideConditions = extractShowHideConditions(filters.additional, refine_search);
              if (showHideConditions.show_only_candidates_with.length > 0) {
                  filter.show_only_candidates_with = showHideConditions.show_only_candidates_with;
              }
              if (showHideConditions.hide_only_candidates_with.length > 0) {
                  filter.hide_only_candidates_with = showHideConditions.hide_only_candidates_with;
              }
  
              // Create the final ordered filter
              const orderedFilter = createOrderedFilter(filter);            
              const isBlankSearch = /^blank search$/i.test(label)
              return {
                  label: isBlankSearch ? label : concatenateAllValues({filter: orderedFilter}),
                  title,
                  requestId,
                  filter: orderedFilter,
                  cn: channel_id,
                  reqParam: reqParam,
                  ...(recentSearchId ? { recentSearchId } : {requestId}),
              };
  
          } catch (error) {
              console.error("Error transforming search data:", error);
              return {
                  label: "",
                  title: "",
                  requestId: "",
                  cn: 1,
                  reqParam: {},
                  filter: {}
              };
          }
      });
  };
  